import React, { createContext, useMemo, useState, useEffect, useContext } from 'react';
import { jsx } from 'react/jsx-runtime';
import SDK from '@ww-digital/xs-sdk';

const XSContext = createContext({});

/**
 * Subclass of XS SDK JS.
 *
 * @class XS
 * @param props - XS props.
 * @param props.baseUrl - XS props.
 * @returns {XS} - XS instance.
 */

class XS extends SDK {}
const createInstance = props => {
  return new XS(props);
};

var LoadingStrategy;

(function (LoadingStrategy) {
  LoadingStrategy["LAZY"] = "lazy";
  LoadingStrategy["EAGER"] = "eager";
})(LoadingStrategy || (LoadingStrategy = {}));

const instances = {};
const XSProvider = props => {
  const {
    children,
    user,
    timeout,
    baseUrl,
    experiments,
    filters,
    plugins,
    isServerSide = false,
    loadingStrategy = LoadingStrategy.EAGER,
    key = 'default'
  } = props;
  const {
    xs,
    finalUserPromise
  } = useMemo(() => {
    let finalUserPromise = null;

    if (instances[key]) {
      return instances[key];
    }

    const xs = new XS({
      baseUrl,
      experiments,
      filters,
      timeout,
      plugins
    });

    if (!isServerSide) {
      if ('then' in user) {
        finalUserPromise = user.then(xs.setUser);
      } else {
        xs.setUser(user);
      }

      if (loadingStrategy === LoadingStrategy.EAGER && !experiments) {
        if (finalUserPromise) {
          finalUserPromise.then(xs.fetchUserExperiments);
        } else {
          xs.fetchUserExperiments();
        }
      }
    }

    instances[key] = {
      xs,
      finalUserPromise
    };
    return instances[key];
  }, [key, baseUrl, experiments, filters, timeout, user, loadingStrategy, isServerSide]);
  const value = {
    xs,
    finalUserPromise,
    loadingStrategy,
    isServerSide
  };
  return jsx(XSContext.Provider, Object.assign({
    value: value
  }, {
    children: children
  }));
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

/**
 * @func withXS
 * @param Component - React component to wrap
 * @returns React component with XSContextProps
 */

const withXS = function (Component) {
  const WithXS = props => {
    const rest = __rest(props, ["forwardedRef"]);

    return jsx(XSContext.Consumer, {
      children: context => jsx(Component, Object.assign({}, rest, context))
    });
  };

  WithXS.displayName = `WithXS(${getDisplayName(Component)})`;
  return React.forwardRef((props, ref) => {
    return jsx(WithXS, Object.assign({}, props, {
      forwardedRef: ref
    }));
  });
};

const getDisplayName = Component => {
  return typeof Component === 'string' ? Component : Component.displayName || Component.name || 'Component';
};

/**
 * @func Experiment
 * @param props {@link ExperimentProps}
 * @returns {React.ReactNode}
 */

const Experiment = props => {
  const {
    name,
    children,
    xs,
    loadingStrategy,
    finalUserPromise,
    fallback = () => null,
    preExposure,
    postExposure
  } = props;
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    if (loadingStrategy === LoadingStrategy.LAZY && !xs.hasValidExperiments) {
      if (finalUserPromise) {
        finalUserPromise.then(xs.fetchUserExperiments);
      } else {
        xs.fetchUserExperiments();
      }
    }
  }, [finalUserPromise, loadingStrategy, xs]);
  useEffect(() => {
    if (xs) {
      xs.onReady().then(() => {
        setIsReady(true);
      });
    }
  }, [xs]);
  useEffect(() => {
    const registerExposure = () => __awaiter(void 0, void 0, void 0, function* () {
      try {
        if (isReady && name) {
          if (preExposure) {
            yield preExposure();
          }

          yield xs.exposeExperiment(name);

          if (postExposure) {
            yield postExposure();
          }
        }
      } catch (e) {
        console.error(e);
      }
    });

    registerExposure();
  }, [preExposure, isReady, name, postExposure, xs]);

  if (!isReady) {
    return fallback();
  } // eslint-disable-next-line @typescript-eslint/no-explicit-any


  return children;
};

var index = withXS(Experiment);

/**
 * Hook to get the current user's experiment and attributes.
 * @function useXS
 * @param {string} experimentId - experiment text id
 * @returns - {isReady, attributes, experiment, variationName, exposeExperiment, getAttribute}
 */

const useXS = experimentId => {
  const emtpyExperiment = useMemo(() => ({}), []);
  const {
    xs
  } = useContext(XSContext);
  const [state, setState] = useState({
    isReady: false,
    attributes: {},
    experiment: emtpyExperiment,
    variationName: ''
  });
  const {
    getAttribute,
    exposeExperiment
  } = xs;
  useEffect(() => {
    xs.onReady().then(() => {
      setState({
        isReady: true,
        attributes: xs.getAttributes(experimentId) || {},
        experiment: xs.getExperiment(experimentId) || emtpyExperiment,
        variationName: xs.getVariation(experimentId) || ''
      });
    });
  }, [emtpyExperiment, experimentId, xs]);
  return Object.assign(Object.assign({}, state), {
    exposeExperiment,
    getAttribute
  });
};

export { index as Experiment, LoadingStrategy, XS, XSContext, XSProvider, createInstance, useXS, withXS };
